import { STRUCTURE_GRID_SIZE } from 'data/constants';
import { PositionType } from 'data/types';
import { computeFootprintGridBounds } from 'utils/footprint-scaling-gesture';

export const FOOTPRINT_CANVAS_OFFSET = 10000;

export const MIN_SHAPE_DIMENSION_FEET = 2;

export type PixelPoint = { x: number; y: number };

export type ShapeEdge = {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  index: number;
  isHorizontal: boolean;
};

export function footprintPixelToCanvas(point: PixelPoint): PixelPoint {
  return {
    x: point.x + FOOTPRINT_CANVAS_OFFSET,
    y: point.y + FOOTPRINT_CANVAS_OFFSET
  };
}

export function footprintGridToPixel(point: PositionType): PixelPoint {
  return footprintPixelToCanvas({
    x: point.x * STRUCTURE_GRID_SIZE,
    y: point.y * STRUCTURE_GRID_SIZE
  });
}

export function screenToGridFeetRaw(
  clientX: number,
  clientY: number,
  svg: SVGSVGElement,
  zoom: number
): PositionType {
  const rect = svg.getBoundingClientRect();
  const x = (clientX - rect.left) / zoom;
  const y = (clientY - rect.top) / zoom;
  return {
    x: (x - FOOTPRINT_CANVAS_OFFSET) / STRUCTURE_GRID_SIZE,
    y: (y - FOOTPRINT_CANVAS_OFFSET) / STRUCTURE_GRID_SIZE
  };
}

export function shapeToPixelPoints(shape: PositionType[]): PixelPoint[] {
  return shape.map(footprintGridToPixel);
}

export function buildClosedPathD(pixelPoints: PixelPoint[]): string {
  if (pixelPoints.length < 3) return '';
  return `${pixelPoints
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`)
    .join(' ')} Z`;
}

export function buildShapeEdges(pixelPoints: PixelPoint[]): ShapeEdge[] {
  if (pixelPoints.length < 3) return [];
  return pixelPoints.map((p, i) => {
    const next = pixelPoints[(i + 1) % pixelPoints.length];
    return {
      x1: p.x,
      y1: p.y,
      x2: next.x,
      y2: next.y,
      index: i,
      isHorizontal: Math.abs(next.x - p.x) >= Math.abs(next.y - p.y)
    };
  });
}

export function moveShapeVertex(
  shape: PositionType[],
  vertexIndex: number,
  point: PositionType
): PositionType[] {
  return shape.map((p, i) => (i === vertexIndex ? { ...p, ...point } : p));
}

export function moveShapeEdgePerpendicular(
  shape: PositionType[],
  edgeIndex: number,
  dx: number,
  dy: number
): PositionType[] {
  const i = edgeIndex;
  const j = (edgeIndex + 1) % shape.length;
  const p1 = shape[i];
  const p2 = shape[j];
  const isHorizontal = Math.abs(p2.x - p1.x) >= Math.abs(p2.y - p1.y);
  return shape.map((p, index) => {
    if (index !== i && index !== j) return p;
    if (isHorizontal) return { ...p, y: p.y + dy };
    return { ...p, x: p.x + dx };
  });
}

export function translateShape(
  shape: PositionType[],
  dx: number,
  dy: number
): PositionType[] {
  return shape.map((p) => ({ ...p, x: p.x + dx, y: p.y + dy }));
}

export function insertShapeVertex(
  shape: PositionType[],
  edgeIndex: number,
  point: PositionType
): PositionType[] {
  const next = [...shape];
  next.splice(edgeIndex + 1, 0, point);
  return next;
}

export type FootprintOutlineDragState =
  | { kind: 'vertex'; vertexIndex: number }
  | { kind: 'vertex-scale'; vertexIndex: number }
  | { kind: 'edge'; edgeIndex: number };

export type FootprintShapeDragState = FootprintOutlineDragState & {
  shapeIndex: number;
};

export type FootprintShapeHoverTarget = {
  shapeIndex: number;
  index: number;
};

export type OutlineRenderData = {
  points: PositionType[];
  pixelPoints: PixelPoint[];
  pathD: string;
  edges: ShapeEdge[];
};

export function getVertexResizeCursor(
  points: PositionType[],
  vertexIndex: number
): string {
  const bbox = computeFootprintGridBounds(points);
  if (!bbox) return 'grab';
  const vertex = points[vertexIndex];
  const isLeft = vertex.x < bbox.centerX;
  const isTop = vertex.y < bbox.centerY;
  return isLeft === isTop ? 'nwse-resize' : 'nesw-resize';
}

export function deleteOutlineVertex(
  outline: PositionType[],
  vertexIndex: number
): PositionType[] {
  const reduced = outline.filter((_, i) => i !== vertexIndex);
  return reduced.length >= 3 ? reduced : [];
}

export function buildOutlineRenderData(
  outline: PositionType[]
): OutlineRenderData | null {
  if (outline.length < 3) return null;
  const pixelPoints = shapeToPixelPoints(outline);
  return {
    points: outline,
    pixelPoints,
    pathD: buildClosedPathD(pixelPoints),
    edges: buildShapeEdges(pixelPoints)
  };
}

export function getOutlineVertexCursor(
  vertexEdited: boolean,
  isDragging: boolean,
  points: PositionType[],
  vertexIndex: number
): string {
  if (!vertexEdited) {
    return getVertexResizeCursor(points, vertexIndex);
  }
  return isDragging ? 'grabbing' : 'grab';
}

export type VertexScaleContext = {
  startPoints: PositionType[];
  anchor: PositionType;
  startDeltaX: number;
  startDeltaY: number;
  width: number;
  height: number;
};

export function beginVertexScale(
  points: PositionType[],
  vertexIndex: number
): VertexScaleContext | null {
  const bbox = computeFootprintGridBounds(points);
  if (!bbox) return null;
  const width = bbox.maxX - bbox.minX;
  const height = bbox.maxY - bbox.minY;
  if (width <= 0 || height <= 0) return null;
  const vertex = points[vertexIndex];
  const anchor = {
    x: vertex.x - bbox.minX >= bbox.maxX - vertex.x ? bbox.minX : bbox.maxX,
    y: vertex.y - bbox.minY >= bbox.maxY - vertex.y ? bbox.minY : bbox.maxY
  };
  return {
    startPoints: points.map((p) => ({ ...p })),
    anchor,
    startDeltaX: vertex.x - anchor.x,
    startDeltaY: vertex.y - anchor.y,
    width,
    height
  };
}

export function applyVertexScale(
  ctx: VertexScaleContext,
  cursor: PositionType
): PositionType[] {
  const rawRatioX = (cursor.x - ctx.anchor.x) / ctx.startDeltaX;
  const rawRatioY = (cursor.y - ctx.anchor.y) / ctx.startDeltaY;
  const ratioX = Math.max(rawRatioX, MIN_SHAPE_DIMENSION_FEET / ctx.width);
  const ratioY = Math.max(rawRatioY, MIN_SHAPE_DIMENSION_FEET / ctx.height);
  return ctx.startPoints.map((p) => ({
    x: Math.round(ctx.anchor.x + (p.x - ctx.anchor.x) * ratioX),
    y: Math.round(ctx.anchor.y + (p.y - ctx.anchor.y) * ratioY)
  }));
}

export function buildShapesRenderData(
  shapes: ReadonlyArray<{ points: PositionType[] }>
): (OutlineRenderData | null)[] {
  return shapes.map((shape) => buildOutlineRenderData(shape.points));
}

export function isSameHoverTarget(
  target: FootprintShapeHoverTarget | null,
  shapeIndex: number,
  index: number
): boolean {
  return (
    target !== null &&
    target.shapeIndex === shapeIndex &&
    target.index === index
  );
}
